import React, { useState, useEffect, useCallback } from "react";
import { ToastContainer } from "react-toastify";
import { countries } from "../components/CountryName";
import {
    PlaneTakeoff, PlaneLanding, Globe,
    MapPin, CreditCard, Plus, Trash2, AlertCircle, ChevronDown, CheckCircle, Edit2
} from "lucide-react";

// Create fresh copy of empty travel
const createFreshTravel = (EMPTY_TRAVEL) => {
    const newTravel = EMPTY_TRAVEL();
    return { ...newTravel };
};

const TravelForm = ({ travels, setTravels, EMPTY_TRAVEL, preview, handleChange, loading, errors, setPreview, onSave }) => {
    const [expandedCards, setExpandedCards] = useState({});
    const [localErrors, setLocalErrors] = useState({});
    const [isEditing, setIsEditing] = useState(false);

    // Ensure travels is always an array
    const safeTravels = Array.isArray(travels) ? travels : [];

    // Initialize expanded state for travel cards
    useEffect(() => {
        const initialExpandedState = {};
        safeTravels.forEach((_, index) => {
            if (window.innerWidth < 768) {
                initialExpandedState[index] = index === 0;
            } else {
                initialExpandedState[index] = true;
            }
        });
        setExpandedCards(initialExpandedState);
    }, [safeTravels.length]);

    const toggleCard = (index) => {
        setExpandedCards(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const addTravel = useCallback(() => {
        if (setTravels && EMPTY_TRAVEL) {
            setTravels(prev => {
                const currentArray = Array.isArray(prev) ? prev : [];
                const newTravel = createFreshTravel(EMPTY_TRAVEL);
                const newArray = [...currentArray, newTravel];
                const newIndex = newArray.length - 1;

                setExpandedCards(prevExpanded => ({
                    ...prevExpanded,
                    [newIndex]: true
                }));

                return newArray;
            });
        }
    }, [setTravels, EMPTY_TRAVEL]);

    const removeTravel = useCallback((index) => {
        if (setTravels && safeTravels.length > 1) {
            setTravels(prev => {
                const currentArray = Array.isArray(prev) ? prev : [];
                const newArray = currentArray.filter((_, i) => i !== index);

                setExpandedCards(prevExpanded => {
                    const newExpanded = {};
                    Object.keys(prevExpanded).forEach(key => {
                        const numKey = parseInt(key);
                        if (numKey < index) {
                            newExpanded[numKey] = prevExpanded[numKey];
                        } else if (numKey > index) {
                            newExpanded[numKey - 1] = prevExpanded[numKey];
                        }
                    });
                    return newExpanded;
                });

                return newArray;
            });
        }
    }, [setTravels, safeTravels.length]);

    const handleChangeField = useCallback((index, field, value) => {
        if (handleChange) {
            handleChange(index, field, value);
        }
    }, [handleChange]);

    const formatDate = (date) => {
        if (!date) return "—";
        return new Date(date).toLocaleDateString("en-GB", {
            day: "2-digit", month: "short", year: "numeric",
        });
    };

    const validateForm = () => {
        const newErrors = {};
        safeTravels.forEach((travel, i) => {
            if (!travel.country) newErrors[`country_${i}`] = "Country is required";
            if (!travel.visaType) newErrors[`visaType_${i}`] = "Visa type is required";
            if (!travel.stayAddress) newErrors[`stayAddress_${i}`] = "Accommodation address is required";
            if (!travel.from) newErrors[`from_${i}`] = "Departure date is required";
            if (!travel.to) newErrors[`to_${i}`] = "Return date is required";
            if (travel.from && travel.to && travel.from > travel.to) {
                newErrors[`to_${i}`] = "Return date must be after departure date";
            }
        });
        setLocalErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm() && onSave) {
            onSave();
        }
    };

    // If in preview mode
    if (preview && !isEditing) {
        return (
            <div className="w-full max-w-4xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-10">
                <ToastContainer position="top-right" autoClose={3000} />

                {/* Header */}
                <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-t-xl sm:rounded-t-2xl md:rounded-t-3xl p-5 sm:p-6 md:p-8 text-white shadow-xl">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div className="flex items-center gap-3 sm:gap-4">
                            <div className="p-2 sm:p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                                <PlaneTakeoff size={24} className="text-blue-300" />
                            </div>
                            <div>
                                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Travel Declaration</h1>
                                <p className="text-blue-100/80 text-[10px] sm:text-xs mt-0.5">Historical International Movements</p>
                            </div>
                        </div>

                    </div>
                </div>

                <div className="bg-white rounded-b-xl sm:rounded-b-2xl md:rounded-b-3xl shadow-xl border-x border-b border-gray-100 p-5 sm:p-6 md:p-8">
                    {preview?.status === "approve" && (
                        <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border border-green-100 flex items-center gap-3">
                            <CheckCircle size={20} className="text-green-600 shrink-0" />
                            <p className="text-sm font-medium text-green-800">Travel history verified and approved</p>
                        </div>
                    )}

                    <div className="space-y-4 sm:space-y-5">
                        {safeTravels.filter(t => t.country).length === 0 ? (
                            <div className="text-center py-8 sm:py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                                <PlaneTakeoff size={40} className="text-gray-300 mx-auto mb-3" />
                                <p className="text-gray-500 text-sm">No travel records added</p>
                            </div>
                        ) : (
                            safeTravels.filter(t => t.country).map((travel, i) => (
                                <div key={i} className="bg-white border border-gray-100 rounded-xl sm:rounded-2xl p-4 sm:p-5 hover:shadow-md transition-all duration-300">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
                                            {i + 1}
                                        </div>
                                        <h3 className="font-bold text-gray-800 text-sm sm:text-base">{travel.country}</h3>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                        <Preview label="Visa Type" value={travel.visaType} />
                                        <Preview label="Departure Date" value={formatDate(travel.from)} />
                                        <Preview label="Return Date" value={formatDate(travel.to)} />
                                        <div className="sm:col-span-2">
                                            <Preview label="Accommodation Address" value={travel.stayAddress} />
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {(preview?.status === "reject" || !preview?.status) && (
                        <div className="flex justify-center pt-6 mt-4 border-t border-gray-100">
                            <button
                                onClick={() => setIsEditing(true)}
                                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 sm:px-8 py-3 rounded-xl font-semibold text-sm transition-all shadow-md"
                            >
                                <Edit2 size={16} /> Edit Travel History
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Edit Mode
    return (
        <div className="w-full max-w-4xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-10">
            <ToastContainer position="top-right" autoClose={3000} />

            {loading && (
                <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-white p-6 rounded-2xl shadow-xl flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
                        <p className="text-sm font-medium text-blue-600">Saving travel records...</p>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-t-xl sm:rounded-t-2xl md:rounded-t-3xl p-5 sm:p-6 md:p-8 text-white shadow-xl">
                <div className="flex items-center gap-3 sm:gap-4">
                    <div className="p-2 sm:p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                        <PlaneTakeoff size={24} className="text-blue-300" />
                    </div>
                    <div>
                        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Travel Declaration</h1>
                        <p className="text-blue-100/80 text-[10px] sm:text-xs mt-0.5">Add your international travel history</p>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-b-xl sm:rounded-b-2xl md:rounded-b-3xl shadow-xl border-x border-b border-gray-100 p-5 sm:p-6 md:p-8">

                {/* Error Display */}
                {(Object.keys(localErrors).length > 0 || errors?.length > 0) && (
                    <div className="mb-6 bg-rose-50 border border-rose-200 p-4 rounded-xl animate-in fade-in slide-in-from-top-4">
                        <div className="flex items-center gap-2 text-rose-700 font-medium mb-2">
                            <AlertCircle size={18} /> Please correct the following:
                        </div>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                            {Object.values(localErrors).map((err, i) => (
                                <li key={i} className="text-rose-600 text-[11px] py-0.5">• {err}</li>
                            ))}
                            {errors?.map((err, i) => (
                                <li key={`err-${i}`} className="text-rose-600 text-[11px] py-0.5">• {err}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
                    {safeTravels.length === 0 ? (
                        <div className="text-center py-8 sm:py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                            <PlaneTakeoff size={40} className="text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500 text-sm">No travel records added</p>
                            <button
                                type="button"
                                onClick={addTravel}
                                className="mt-3 text-blue-600 text-sm font-medium flex items-center gap-1 mx-auto"
                            >
                                <Plus size={16} /> Add your first travel record
                            </button>
                        </div>
                    ) : (
                        safeTravels.map((travel, index) => (
                            <div key={index} className="bg-white border border-gray-200 rounded-xl sm:rounded-2xl shadow-sm hover:shadow-md transition-all overflow-hidden">
                                {/* Card Header - Clickable on mobile */}
                                <div
                                    className="flex justify-between items-center p-4 sm:p-5 cursor-pointer md:cursor-default bg-gray-50/50"
                                    onClick={() => toggleCard(index)}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
                                            {index + 1}
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-800 text-sm sm:text-base">
                                                {travel.country || "New Travel Record"}
                                            </h3>
                                            {travel.visaType && (
                                                <p className="text-[10px] sm:text-xs text-gray-500">
                                                    {travel.visaType}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            className="md:hidden text-gray-400"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleCard(index);
                                            }}
                                        >
                                            <ChevronDown size={18} className={`transition-transform ${expandedCards[index] ? 'rotate-180' : ''}`} />
                                        </button>
                                        {safeTravels.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeTravel(index);
                                                }}
                                                className="text-rose-400 hover:text-rose-600 p-1 transition-colors"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Card Content */}
                                <div className={`
                                    transition-all duration-300 overflow-hidden
                                    ${expandedCards[index] !== false ? 'max-h-[2000px] opacity-100' : 'max-h-0 md:max-h-[2000px] md:opacity-100'}
                                `}>
                                    <div className="p-4 sm:p-5 pt-0 sm:pt-0 border-t border-gray-100">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                                            <div>
                                                <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 ml-1 flex items-center gap-2">
                                                    <Globe size={16} /> Country <span className="text-rose-500">*</span>
                                                </label>
                                                <div className="relative">
                                                    <select
                                                        value={travel.country}
                                                        onChange={(e) => handleChangeField(index, "country", e.target.value)}
                                                        className={`w-full bg-gray-50 border rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all appearance-none cursor-pointer text-sm
                                                            ${localErrors[`country_${index}`] ? "border-rose-300 bg-rose-50/30" : "border-gray-200"}`}
                                                    >
                                                        <option value="">Select Country</option>
                                                        {countries.map((c) => <option key={c} value={c}>{c}</option>)}
                                                    </select>
                                                    <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                                </div>
                                                {localErrors[`country_${index}`] && (
                                                    <p className="text-rose-500 text-[10px] mt-1">{localErrors[`country_${index}`]}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 ml-1 flex items-center gap-2">
                                                    <CreditCard size={16} /> Visa Type <span className="text-rose-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={travel.visaType || ""}
                                                    onChange={(e) => handleChangeField(index, "visaType", e.target.value)}
                                                    placeholder="e.g., Tourist, Business, Student"
                                                    className={`w-full bg-gray-50 border rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm
                                                        ${localErrors[`visaType_${index}`] ? "border-rose-300 bg-rose-50/30" : "border-gray-200"}`}
                                                />
                                                {localErrors[`visaType_${index}`] && (
                                                    <p className="text-rose-500 text-[10px] mt-1">{localErrors[`visaType_${index}`]}</p>
                                                )}
                                            </div>

                                            <div className="md:col-span-2">
                                                <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 ml-1 flex items-center gap-2">
                                                    <MapPin size={16} /> Accommodation Address <span className="text-rose-500">*</span>
                                                </label>
                                                <textarea
                                                    rows={2}
                                                    value={travel.stayAddress || ""}
                                                    onChange={(e) => handleChangeField(index, "stayAddress", e.target.value)}
                                                    placeholder="Hotel name, street address, city where you stayed"
                                                    className={`w-full bg-gray-50 border rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm resize-none
                                                        ${localErrors[`stayAddress_${index}`] ? "border-rose-300 bg-rose-50/30" : "border-gray-200"}`}
                                                />
                                                {localErrors[`stayAddress_${index}`] && (
                                                    <p className="text-rose-500 text-[10px] mt-1">{localErrors[`stayAddress_${index}`]}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 ml-1 flex items-center gap-2">
                                                    <PlaneTakeoff size={16} /> Departure Date <span className="text-rose-500">*</span>
                                                </label>
                                                <input
                                                    type="date"
                                                    value={travel.from || ""}
                                                    onChange={(e) => handleChangeField(index, "from", e.target.value)}
                                                    max={new Date().toISOString().split('T')[0]}
                                                    className={`w-full bg-gray-50 border rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm
                                                        ${localErrors[`from_${index}`] ? "border-rose-300 bg-rose-50/30" : "border-gray-200"}`}
                                                />
                                                {localErrors[`from_${index}`] && (
                                                    <p className="text-rose-500 text-[10px] mt-1">{localErrors[`from_${index}`]}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 ml-1 flex items-center gap-2">
                                                    <PlaneLanding size={16} /> Return Date <span className="text-rose-500">*</span>
                                                </label>
                                                <input
                                                    type="date"
                                                    value={travel.to || ""}
                                                    onChange={(e) => handleChangeField(index, "to", e.target.value)}
                                                    min={travel.from}
                                                    max={new Date().toISOString().split('T')[0]}
                                                    className={`w-full bg-gray-50 border rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm
                                                        ${localErrors[`to_${index}`] ? "border-rose-300 bg-rose-50/30" : "border-gray-200"}`}
                                                />
                                                {localErrors[`to_${index}`] && (
                                                    <p className="text-rose-500 text-[10px] mt-1">{localErrors[`to_${index}`]}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 pt-4">
                        <button
                            type="button"
                            onClick={addTravel}
                            className="flex-1 flex items-center justify-center gap-2 bg-white border-2 border-dashed border-gray-300 text-gray-600 hover:border-blue-500 hover:text-blue-600 py-3 rounded-xl font-semibold text-sm transition-all"
                        >
                            <Plus size={18} /> Add Another Trip
                        </button>

                    </div>
                </form>
            </div>
        </div>
    );
};

/* UI HELPERS */
const Preview = ({ label, value }) => (
    <div className="flex flex-col gap-0.5">
        <span className="text-[9px] sm:text-[10px] uppercase tracking-wider font-bold text-gray-400">{label}</span>
        <span className="text-gray-800 font-semibold text-sm break-words">{value || "—"}</span>
    </div>
);

export default TravelForm;